// contexts/identityAndTenancy/index.js
//
// Published interface of the Identity & Tenancy context on the frontend. Everything outside imports
// from here and nothing deeper — the layers behind it are internal.
//
// THE BACKEND CONTEXT OWNS THE LANGUAGE. `functions/contexts/identityAndTenancy` publishes the
// definition and this conforms to it. The two are deliberately not a shared kernel: a shared one
// would make every change a negotiation between the runtimes importing it, which is how the mud gets
// rebuilt with better tooling. What crosses the wire is a DTO, and this side reconstructs from it.
//
// PERSON AND TEAMS ARE ALL THAT IS HERE SO FAR. User exists only as far as its time zone preference;
// the rest of it, clinic and roles are still loose code elsewhere under `src/` and land here when an
// outcome needs them.
//
// NOTHING HERE MAY REACH APP CODE. `OfflineAccessUtils` and `useTime()` sit beneath `store/auth` and
// import this interface, so anything here that reads the store closes a cycle. The signed-in account
// therefore arrives the other way: the store records it into this context's session (see
// `infrastructure/session.js`), and a write takes its storage from the caller.

import { changeTimeZonePreference as changeTimeZonePreferenceIn, userTimeZone } from './application/timeZones';
import { deviceTimeZone, recordSignedInAccount, signedInAccount } from './infrastructure/session';
import { userRepository } from './infrastructure/userRepository';

// Rows carry `identity` as a plain object whether the server resolved it or the offline read did,
// so there is one way in and one shape to come in from. The constructor behind it is deliberately
// absent from this interface — see `index.test.js`.
export { toAccountIdentityDto, toPersonIdentity } from './application/personIdentity';

// Published because a value object without its equality leaves a caller comparing field by field,
// which is the hand-assembly the value exists to prevent.
export { isSamePersonIdentity } from './domain/model/PersonIdentity';

// The teams people work in: ids, the role defaults, and the rule that picks one or the other. Pure,
// as everything here must be; reading and saving a membership is the host's, for the reason above.
export { TEAMS, TEAM_IDS, DEFAULT_TEAMS_BY_ROLE } from './domain/constants/teams';
export { teamsOf } from './domain/model/teamMembership';

// Whose time zone it is. Which zone a rule uses stays the consuming context's decision.
export { userTimeZone };

// For `store/auth`'s `setUserDetails` only, until the store is gone.
export { recordSignedInAccount };

/**
 * The zone the signed-in user reads and enters dates in: their preference, or their device's.
 * @returns {string} IANA zone
 */
export const viewerTimeZone = () => userTimeZone(signedInAccount(), deviceTimeZone());

/**
 * Sets or clears a user's preference, refusing anything that is not a zone.
 *
 * @param {string} userId
 * @param {string|null} zone
 * @param {{writeAccountFields: (userId: string, fields: Object) => Promise<void>}} storage - lent by
 *   the caller until the store is gone, because `src/fb` reaches it
 * @returns {Promise<void>}
 */
export const changeTimeZonePreference = (userId, zone, { writeAccountFields }) => (
  changeTimeZonePreferenceIn(userRepository(writeAccountFields))(userId, zone)
);
